import { useState, useEffect } from 'react';
import type { ActionVerb, ClientMsg, KillMethod, Lang, PlayerView } from '../../../shared/src/types.js';
import { ROLE_BY_ID } from '../../../shared/src/roles/index.js';
import { DeathScene } from '../components/DeathScene.js';
import { PhaseFlash } from '../components/PhaseFlash.js';
import { DiscussionTimerView } from '../components/DiscussionTimer.js';
import { ExitRoomButton } from '../components/ExitRoomButton.js';

interface Props {
  t: Lang;
  view: PlayerView;
  send: (msg: ClientMsg) => void;
}

// Label a death by HOW the player died — a lynch and a vigilante's day
// execute are day events, never «НОЧЬЮ ПОГИБ» (audit P2 mislabel fix).
function deathLabel(method: KillMethod, t: Lang): string {
  if (method === 'vote') return t === 'ru' ? 'ПОВЕШЕН' : 'ПОВІШЕНО';
  if (method === 'vigilante') return t === 'ru' ? 'КАЗНЁН ДНЁМ' : 'СТРАЧЕНО ВДЕНЬ';
  return t === 'ru' ? 'НОЧЬЮ ПОГИБ' : 'УНОЧІ ЗАГИНУВ';
}

const DAY_VERB_COPY: Record<string, Record<Lang, { call: string; prompt: string; submit: string }>> = {
  execute: {
    ru: { call: 'КАЗНЬ', prompt: 'РАЗ ЗА ИГРУ — ВЫСТРЕЛИТЬ', submit: 'КАЗНИТЬ' },
    uk: { call: 'СТРАТА', prompt: 'РАЗ ЗА ГРУ — ВИСТРІЛИТИ', submit: 'СТРАТИТИ' },
  },
  shield: {
    ru: { call: 'АДВОКАТСКИЙ ЩИТ', prompt: 'ОТМЕНИТЬ ГОЛОСОВАНИЕ', submit: 'ЗАЩИТИТЬ' },
    uk: { call: 'АДВОКАТСЬКИЙ ЩИТ', prompt: 'СКАСУВАТИ ГОЛОСУВАННЯ', submit: 'ЗАХИСТИТИ' },
  },
  reveal: {
    ru: { call: 'РАСКРЫТЬ', prompt: 'ОБЪЯВИТЬ РОЛЬ ПУБЛИЧНО', submit: 'ОТКРЫТЬ' },
    uk: { call: 'ВІДКРИТИ', prompt: 'ОГОЛОСИТИ РОЛЬ ПУБЛІЧНО', submit: 'ВІДКРИТИ' },
  },
};

export function Day({ t, view, send }: Props) {
  const phase = view.room.phase;
  const me = view.me;
  const [vote, setVote] = useState<string | null>(null);
  const [dayActionTarget, setDayActionTarget] = useState<string | null>(null);
  const [dayActionFired, setDayActionFired] = useState(false);

  // Reset selection only when the day rolls over (a fresh ballot/abilities),
  // NOT on every phase change — otherwise the optimistic vote/fire would be
  // blown away when day-discussion → day-vote → day-result transitions arrive.
  useEffect(() => {
    setVote(null);
    setDayActionTarget(null);
    setDayActionFired(false);
  }, [view.room.day]);

  // Reconcile the optimistic local vote against the server's record. The server
  // persists the ballot (it survives reconnect), but the projection only echoes
  // it back via `myVoteTargetId`: `undefined` = not voted, `null` = abstained,
  // string = the seat voted for. Mirrors Night.tsx's myPendingVerbs reconcile.
  useEffect(() => {
    if (view.myVoteTargetId !== undefined) setVote(view.myVoteTargetId);
  }, [view.myVoteTargetId]);

  const dayPrompt = view.prompt && view.prompt.ability.phase === 'day' ? view.prompt : null;

  // Reconcile the day-ability "fired" lock against server truth — AUTHORITATIVE
  // (sets true AND false), like the vote reconcile above and Night.tsx. The
  // optimistic onClick sets it true for instant feedback; if the server then
  // silently rejects the action (e.g. vigilante execute on a now-dead target),
  // the verb won't appear in myPendingVerbs and this clears the lock so the
  // picker reappears instead of stranding the player on "ОТПРАВЛЕНА".
  // `myPendingVerbs` already includes day abilities (server projects
  // pendingDayActions too), so a remount/reconnect won't re-prompt a consumed one.
  useEffect(() => {
    if (!dayPrompt) return;
    setDayActionFired(!!view.myPendingVerbs?.includes(dayPrompt.ability.key));
  }, [view.myPendingVerbs, dayPrompt]);

  // Defensive — Day is only routed here once roles have been dealt. Placed
  // after all hooks so the hook order stays stable across renders.
  if (!me.role) return null;

  // Mirror the server's vote-weight rule: a hidden mayor still counts as 1
  // until they fire their reveal (server: predicates.ts effectiveVoteWeight).
  // Otherwise the role's declared voteWeight (or 1) wins.
  const isHiddenMayor = me.role.id === 'mayor' && !me.revealedAsMayor;
  const effectiveWeight = isHiddenMayor ? 1 : me.role.voteWeight ?? 1;

  // Cinematic overlays:
  //  - On the first frame of day-discussion, if someone died last night,
  //    play a method-specific death scene (knife/fire/claw…). Multi-kill
  //    nights play the first death's scene; the announcement list below
  //    names every victim.
  //  - When the host opens day-vote, play a quick gavel-slam.
  const deaths = view.room.deaths;
  const nameOf = (id: string) =>
    view.room.players.find((p) => p.id === id)?.name ?? '';
  // Cinematic keys off the NEWEST (last) death so that a vigilante day-execute
  // appended after the morning batch triggers a fresh scene for the new victim.
  // The signature includes ALL victim ids so it also changes when the array grows.
  const newestDeath = deaths.length > 0 ? deaths[deaths.length - 1] : undefined;
  const elimVictim = newestDeath ? nameOf(newestDeath.playerId) : '';
  const showDeath =
    phase === 'day-discussion' && newestDeath && elimVictim;
  const deathSig = newestDeath
    ? `D${view.room.day}-${deaths.map((d) => d.playerId).join('+')}-${newestDeath.method}`
    : '';

  // Day-result panel uses the first (and only) death from resolveVote which
  // always produces a single-element array with method:'vote'. Renamed from
  // headDeath → firstDeath so the name doesn't conflict with the cinematic's
  // newestDeath (last element).
  const firstDeath = deaths[0];
  // Lynched player's role, surfaced for the day-result beat unless the server
  // flagged it hidden (janitor). `roleId` is enriched on the projection.
  const elimRole =
    firstDeath && firstDeath.roleId && !firstDeath.roleHidden
      ? ROLE_BY_ID[firstDeath.roleId]
      : null;

  // Aggregate vote progress for the player tally — server only tells players
  // WHO voted (votedIds), never the targets, so this is a count, not a race.
  const alivePlayers = view.room.players.filter((p) => p.alive).length;
  const votedCount = view.room.votedIds.length;

  // Localized echo of the day-ability target, for the post-fire confirmation.
  const dayActionTargetName = dayActionTarget
    ? view.room.players.find((p) => p.id === dayActionTarget)?.name ?? ''
    : '';

  return (
    <div className="scr scr-vote">
      <div className="bg-day" />

      {showDeath && (
        <DeathScene
          t={t}
          victimName={elimVictim}
          method={newestDeath!.method}
          signature={deathSig}
        />
      )}
      {phase === 'day-vote' && (
        <PhaseFlash
          t={t}
          kind="vote-start"
          signature={`vote-${view.room.day}`}
        />
      )}

      {phase === 'day-discussion' && view.room.discussionTimer && (
        <div className="disc-timer-panel">
          <DiscussionTimerView t={t} timer={view.room.discussionTimer} />
        </div>
      )}

      <div className="vote-head">
        <div className="phase-pill phase-day">
          <span className="sun-dot" />
          <span className="sub">
            {t === 'ru' ? 'ДЕНЬ · ' : 'ДЕНЬ · '}
            {String(view.room.day).padStart(2, '0')}
          </span>
        </div>
        <div className="display vote-title">
          {phase === 'day-discussion'
            ? t === 'ru' ? 'ОБСУЖДЕНИЕ' : 'ОБГОВОРЕННЯ'
            : phase === 'day-vote'
              ? t === 'ru' ? 'КОГО ПОВЕСИТЬ?' : 'КОГО ПОВІСИТИ?'
              : t === 'ru' ? 'РЕЗУЛЬТАТ' : 'РЕЗУЛЬТАТ'}
        </div>
        <div className="mono vote-sub">
          {/* Combined morning announcement — EVERY death since the last day
              (multi-kill nights: mafia + maniac, rampage, ignite…), each
              labelled by how the player died and revealing the role unless
              the janitor cleaned the body. */}
          {deaths.length > 0 && phase === 'day-discussion' &&
            deaths.map((d) => {
              const role = d.roleId && !d.roleHidden ? ROLE_BY_ID[d.roleId] : null;
              return (
                <div key={d.playerId}>
                  {deathLabel(d.method, t)}{' '}
                  <strong>{nameOf(d.playerId)}</strong>
                  {role && (
                    <em style={{ marginLeft: 8, color: role.accent }}>
                      · {role.title[t]}
                    </em>
                  )}
                  {d.roleHidden && (
                    <em style={{ marginLeft: 8 }}>{t === 'ru' ? '· РОЛЬ СКРЫТА' : '· РОЛЬ ПРИХОВАНА'}</em>
                  )}
                </div>
              );
            })}
          {phase === 'day-vote' && (t === 'ru' ? 'ОДИН ГОЛОС НА ИГРОКА' : 'ОДИН ГОЛОС НА ГРАВЦЯ')}
          {phase === 'day-result' && firstDeath &&
            (t === 'ru' ? 'ПОВЕШЕН' : 'ПОВІШЕНО')}
        </div>
      </div>

      {/* Day-phase ability bar — vigilante/lawyer/priest pick a target;
          mayor.reveal is targetless and skips the picker entirely. */}
      {dayPrompt && me.alive && !dayActionFired && (() => {
        const isTargetless =
          !!dayPrompt.ability.targetless || dayPrompt.ability.shape === 'targetless';
        // The mayor's self-reveal shares the generic `reveal` verb with the
        // priest, so disambiguate by role: spell out the ×2 vote consequence.
        const isMayorReveal = dayPrompt.ability.key === 'reveal' && me.role.id === 'mayor';
        const promptText = isMayorReveal
          ? t === 'ru' ? 'РАСКРОЙТЕСЬ — ВАШ ГОЛОС СТАНЕТ ×2' : 'ВІДКРИЙТЕСЬ — ВАШ ГОЛОС СТАНЕ ×2'
          : DAY_VERB_COPY[dayPrompt.ability.key]?.[t]?.prompt ?? '';
        return (
          <div className="day-ability">
            <div className="sub" style={{ color: me.role.accent, fontSize: 10, letterSpacing: '.32em' }}>
              {DAY_VERB_COPY[dayPrompt.ability.key]?.[t]?.call ?? dayPrompt.ability.key}
            </div>
            <div className="mono day-ability-prompt">
              {promptText}
            </div>
            {!isTargetless && (
              <div className="day-ability-targets">
                {view.room.players
                  .filter((p) => p.id !== me.id)
                  .map((p) => (
                    <button
                      key={p.id}
                      className={`pring-btn ${!p.alive ? 'dead' : ''} ${dayActionTarget === p.id ? 'selected' : ''}`}
                      disabled={!dayPrompt.targets.includes(p.id)}
                      aria-label={p.name}
                      aria-pressed={dayActionTarget === p.id}
                      onClick={() => setDayActionTarget(p.id)}
                    >
                      <div className="pring-name display">{p.name}</div>
                    </button>
                  ))}
              </div>
            )}
            <button
              className="btn btn-blood"
              disabled={!isTargetless && !dayActionTarget}
              onClick={() => {
                if (!isTargetless && !dayActionTarget) return;
                send({
                  kind: 'player:action',
                  verb: dayPrompt.ability.key as ActionVerb,
                  ...(isTargetless ? {} : { targetId: dayActionTarget! }),
                });
                setDayActionFired(true);
              }}
            >
              <span className="display">
                {DAY_VERB_COPY[dayPrompt.ability.key]?.[t]?.submit ?? '?'}
              </span>
            </button>
          </div>
        );
      })()}

      {/* Confirmed echo — once the ability is fired (or reconciled from the
          server), show what was sent instead of silently hiding the block. */}
      {dayPrompt && me.alive && dayActionFired && (
        <div className="day-ability">
          <div className="mono day-ability-prompt" style={{ color: me.role.accent }}>
            {(DAY_VERB_COPY[dayPrompt.ability.key]?.[t]?.call ?? dayPrompt.ability.key)}
            {t === 'ru' ? ' ОТПРАВЛЕНА' : ' НАДІСЛАНА'}
            {dayActionTargetName && (
              <strong style={{ marginLeft: 6 }}>· {dayActionTargetName}</strong>
            )}
          </div>
        </div>
      )}

      {/* Day-result beat — dedicated panel instead of the stale vote tally.
          Reveals the lynched player's role (faction accent) unless hidden. */}
      {phase === 'day-result' ? (
        <div className="day-result-panel">
          {firstDeath ? (
            <>
              <div className="display" style={{ fontSize: 28, letterSpacing: '.06em' }}>
                {nameOf(firstDeath.playerId)}
              </div>
              {elimRole ? (
                <div
                  className="display"
                  style={{ color: elimRole.accent, fontSize: 14, letterSpacing: '.18em', marginTop: 6 }}
                >
                  {elimRole.title[t]}
                </div>
              ) : (
                <div className="mono" style={{ opacity: 0.6, marginTop: 6, letterSpacing: '.14em' }}>
                  {t === 'ru' ? 'РОЛЬ СКРЫТА' : 'РОЛЬ ПРИХОВАНА'}
                </div>
              )}
            </>
          ) : (
            <div className="mono" style={{ opacity: 0.7, letterSpacing: '.14em', textAlign: 'center' }}>
              {t === 'ru' ? 'СЕГОДНЯ НИКОГО НЕ КАЗНИЛИ' : 'СЬОГОДНІ НІКОГО НЕ СТРАТИЛИ'}
            </div>
          )}
        </div>
      ) : (
        <>
          {/* Dead players watch as spectators — point them at the dead chat. */}
          {!me.alive && (
            <div className="day-spectator mono" style={{ opacity: 0.85, letterSpacing: '.1em', padding: '10px 4px' }}>
              <div className="display" style={{ fontSize: 13, letterSpacing: '.18em' }}>
                {t === 'ru' ? 'ВЫ ВЫБЫЛИ · НАБЛЮДЕНИЕ' : 'ВИ ВИБУЛИ · СПОСТЕРЕЖЕННЯ'}
              </div>
              <div style={{ marginTop: 4, opacity: 0.7 }}>
                {t === 'ru'
                  ? `В ЖИВЫХ ${alivePlayers} · ПИШИТЕ В ЧАТ МЁРТВЫХ`
                  : `ЖИВИХ ${alivePlayers} · ПИШІТЬ У ЧАТ МЕРТВИХ`}
              </div>
            </div>
          )}

          {/* Aggregate vote progress — server hides targets from players, so
              this is a count of who has cast a ballot, NOT a per-candidate race. */}
          {phase === 'day-vote' && (
            <div className="mono day-vote-count" style={{ opacity: 0.7, letterSpacing: '.14em', padding: '4px 4px 8px' }}>
              {votedCount} / {alivePlayers} {t === 'ru' ? 'ПРОГОЛОСОВАЛИ' : 'ПРОГОЛОСУВАЛИ'}
            </div>
          )}

          <div className="tally-board">
            {view.room.players.map((p) => {
              const mine = vote === p.id;
              const isAlive = p.alive;
              // `votedIds` lists every player who has cast a vote this round
              // (server hides the target). The dot just marks "has voted" —
              // neutral, NOT a per-candidate count or an approval check.
              const hasVoted = view.room.votedIds.includes(p.id);
              return (
                <button
                  key={p.id}
                  className={`tally-row ${mine ? 'mine' : ''} ${hasVoted ? 'voted' : ''}`}
                  disabled={!isAlive || phase !== 'day-vote' || !me.alive || p.id === me.id}
                  aria-label={t === 'ru' ? `Голосовать за ${p.name}` : `Голосувати за ${p.name}`}
                  aria-pressed={mine}
                  onClick={() => {
                    // Tap the already-selected row to clear the vote (abstain),
                    // mirroring Night's tap-to-clear target pattern.
                    if (vote === p.id) {
                      setVote(null);
                      send({ kind: 'player:vote', targetId: null });
                    } else {
                      setVote(p.id);
                      send({ kind: 'player:vote', targetId: p.id });
                    }
                  }}
                >
                  <div className="tally-finger">
                    <svg width="22" height="22" viewBox="0 0 22 22">
                      <path
                        d="M2 11 L11 6 L11 8 L20 8 L20 14 L11 14 L11 16 Z"
                        fill={mine ? '#ff2a3c' : '#0a0a0c'}
                      />
                    </svg>
                  </div>
                  <div className="tally-name display" style={{ textDecoration: isAlive ? 'none' : 'line-through' }}>
                    {p.name}
                  </div>
                  <div
                    className="tally-flap mono"
                    aria-label={hasVoted ? (t === 'ru' ? 'проголосовал' : 'проголосував') : undefined}
                  >
                    <span aria-hidden="true" style={{ fontSize: 11, letterSpacing: '.08em', opacity: hasVoted ? 0.85 : 0 }}>
                      {hasVoted ? (t === 'ru' ? 'ГОЛОС' : 'ГОЛОС') : ''}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </>
      )}

      <div className="vote-foot">
        {phase === 'day-vote' && me.alive ? (
          <>
            <button
              className="btn btn-ghost"
              onClick={() => {
                setVote(null);
                send({ kind: 'player:vote', targetId: null });
              }}
            >
              <span className="display">
                {vote
                  ? t === 'ru' ? 'ОТМЕНИТЬ ГОЛОС' : 'СКАСУВАТИ ГОЛОС'
                  : t === 'ru' ? 'ВОЗДЕРЖАТЬСЯ' : 'УТРИМАТИСЬ'}
              </span>
            </button>
            <div className="reveal-hint mono" style={{ flex: 1 }}>
              {vote ? (
                <>
                  {effectiveWeight > 1
                    ? `${t === 'ru' ? 'ВАШ ГОЛОС' : 'ВАШ ГОЛОС'} ×${effectiveWeight}`
                    : t === 'ru' ? 'ГОЛОС ПОДАН · МОЖНО ПОМЕНЯТЬ' : 'ГОЛОС ПОДАНО · МОЖНА ЗМІНИТИ'}
                </>
              ) : view.myVoteTargetId === null ? (
                t === 'ru' ? 'ВЫ ВОЗДЕРЖАЛИСЬ' : 'ВИ УТРИМАЛИСЯ'
              ) : isHiddenMayor ? (
                t === 'ru' ? 'РАСКРОЙТЕСЬ — ГОЛОС ×2' : 'ВІДКРИЙТЕСЬ — ГОЛОС ×2'
              ) : effectiveWeight > 1 ? (
                `${t === 'ru' ? 'ВАШ ГОЛОС' : 'ВАШ ГОЛОС'} ×${effectiveWeight}`
              ) : (
                t === 'ru' ? 'ВЫБЕРИ ИГРОКА' : 'ОБЕРИ ГРАВЦЯ'
              )}
            </div>
          </>
        ) : (
          <div className="reveal-hint mono" style={{ gridColumn: 'span 2', flex: 1 }}>
            {phase === 'day-discussion' && me.alive &&
              (t === 'ru' ? 'ВЕДУЩИЙ НАЧНЁТ ГОЛОСОВАНИЕ' : 'ВЕДУЧИЙ ПОЧНЕ ГОЛОСУВАННЯ')}
            {phase === 'day-result' && me.alive && (t === 'ru' ? 'ВЕДУЩИЙ НАЧНЁТ НОЧЬ' : 'ВЕДУЧИЙ ПОЧНЕ НІЧ')}
            {!me.alive &&
              (t === 'ru' ? `НАБЛЮДЕНИЕ · В ЖИВЫХ ${alivePlayers}` : `СПОСТЕРЕЖЕННЯ · ЖИВИХ ${alivePlayers}`)}
          </div>
        )}
      </div>

      <ExitRoomButton t={t} send={send} midGame />
    </div>
  );
}
